import React, { useEffect, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { DocumentUpload } from './components/DocumentUpload.tsx';
import { TemplateRecommendation } from './components/TemplateRecommendation.tsx';
import { documentApi } from './services/api.ts';
import { StoryboardEditor } from './components/StoryboardEditor.tsx';
import { EnhancedChatAssistant } from './components/EnhancedChatAssistant.tsx';
import { PreviewMode } from './components/PreviewMode.tsx';
import TopicSelection from './components/TopicSelection.tsx';
import GuideResults from './components/GuideResults.tsx';
import ProductSearch from './components/ProductSearch.tsx';
import { ProductWalkthroughSource } from './components/ProductWalkthroughSource.tsx';
import { TemplateAndSettings } from './components/TemplateAndSettings.tsx';
import { TemplateRecommendation as TemplateRecommendationType, Storyboard, StoryboardStep, TopicSuggestion, GeneratedGuide, SearchResults, GenerationSettings, GuideTemplate } from './types/index.ts';
import { logger } from './lib/logger.ts';
import { apiV1Url } from './lib/apiBaseUrl.ts';
import { creatorFetch } from './services/creatorFetch.ts';
import { ensurePfReady } from './services/ensurePfReady.ts';
import { useDatabaseHealth } from './hooks/useDatabaseHealth.ts';

type MakerProduct = {
  productId: string;
  productName: string;
  productAlias?: string;
};

type MakerBrand = {
  brandId: string;
  brandName: string;
  brandAlias?: string;
  simpiBrandId?: string | null;
  role?: string;
  products?: MakerProduct[];
};

type MakerMeResponse = {
  user?: {
    entraOid: string;
    email?: string;
    name?: string;
  };
  brands?: MakerBrand[];
  defaultBrandId?: string;
};

type SourceKind = 'manual' | 'webpage' | 'search' | 'product_walkthrough';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      staleTime: 5 * 60 * 1000 // 5 minutes
    }
  }
});

const DEFAULT_GENERATION_SETTINGS: GenerationSettings = {
  template: 'product_guide',
  style: 'auto',
  preferredLanguage: 'auto',
  filters: {
    skipDisclaimers: true,
    skipDuplicateLanguages: true,
    skipToc: true
  },
  maxTopics: 20
};

const SIMPI_LOGO_URL = 'https://app.simpi.com/assets/svg/simpi-logo.svg';

const START_PAGE_BACKGROUND: React.CSSProperties = {
  backgroundColor: '#f8fafc',
  backgroundImage: [
    'radial-gradient(circle at 15% 18%, rgba(59, 130, 246, 0.12), transparent 28%)',
    'radial-gradient(circle at 82% 12%, rgba(20, 184, 166, 0.10), transparent 30%)',
    'radial-gradient(circle at 50% 82%, rgba(148, 163, 184, 0.14), transparent 34%)',
    'linear-gradient(rgba(15, 23, 42, 0.035) 1px, transparent 1px)',
    'linear-gradient(90deg, rgba(15, 23, 42, 0.035) 1px, transparent 1px)',
    'linear-gradient(135deg, #f8fafc 0%, #eef6ff 45%, #f7fbf8 100%)'
  ].join(', '),
  backgroundSize: 'auto, auto, auto, 44px 44px, 44px 44px, auto'
};

function applyIntentDefaults(template: GuideTemplate, current?: GenerationSettings): GenerationSettings {
  const base = current || DEFAULT_GENERATION_SETTINGS;
  return {
    ...base,
    template,
    maxTopics: template === 'quickstart' ? 8 : 20
  };
}

const mockStoryboard: Storyboard = {
  id: 'storyboard_1',
  headline: 'Product Quickstart Guide',
  templateType: 'Quickstart',
  documentSource: {
    id: 'doc_123',
    title: 'Product Manual v2.1',
    uploadDate: '2024-01-15T10:00:00Z'
  },
  metadata: {
    createdAt: '2024-01-15T10:00:00Z',
    version: '1.0',
    author: 'user_123',
    confidence: 0.85,
    validationStatus: 'passed'
  },
  steps: [
    {
      id: 'step_1',
      order: 1,
      onscreenText: 'Connect the power adapter to your device',
      ttsText: 'First, locate the power adapter that came with your device and plug it into the power port.',
      visualSuggestion: 'Close-up view of the power adapter being plugged into the device port',
      sourceDocReference: {
        docId: 'doc_123',
        page: 5,
        section: 'Getting Started',
        confidence: 0.92
      },
      validation: {
        completeness: 'high',
        safety: 'verified',
        clarity: 'good'
      },
      changeHistory: []
    },
    {
      id: 'step_2',
      order: 2,
      onscreenText: 'Press the power button to turn on the device',
      ttsText: 'Now press and hold the power button for 2 seconds to turn on your device.',
      visualSuggestion: 'Highlight the power button location with an arrow or circle',
      sourceDocReference: {
        docId: 'doc_123',
        page: 5,
        section: 'Getting Started',
        confidence: 0.88
      },
      validation: {
        completeness: 'high',
        safety: 'verified',
        clarity: 'excellent'
      },
      changeHistory: []
    },
    {
      id: 'step_3',
      order: 3,
      onscreenText: 'Wait for the device to complete its startup process',
      ttsText: 'The device will now start up. Please wait for the startup process to complete, which may take up to 30 seconds.',
      visualSuggestion: 'Show the device startup screen or loading indicator',
      sourceDocReference: {
        docId: 'doc_123',
        page: 6,
        section: 'Initial Setup',
        confidence: 0.90
      },
      validation: {
        completeness: 'high',
        safety: 'verified',
        clarity: 'good'
      },
      changeHistory: []
    }
  ]
};

// New interfaces for the updated flow


function App() {
  const launchParams = React.useMemo(() => {
    if (typeof window === 'undefined') {
      return {
        brandId: null as string | null,
        dashboardBrandId: null as string | null,
        simpiBrandId: null as string | null,
        productId: null as string | null,
        brandAlias: null as string | null,
        productAlias: null as string | null,
        returnUrl: null as string | null
      };
    }
    const params = new URLSearchParams(window.location.search);
    return {
      brandId: params.get('brandId'),
      dashboardBrandId: params.get('dashboardBrandId'),
      simpiBrandId: params.get('simpiBrandId'),
      productId: params.get('productId'),
      brandAlias: params.get('brandAlias'),
      productAlias: params.get('productAlias'),
      returnUrl: params.get('returnUrl')
    };
  }, []);

  const [currentStep, setCurrentStep] = useState<'home' | 'source_selection' | 'upload' | 'search' | 'product_walkthrough_source' | 'analyze' | 'recommend' | 'edit' | 'preview' | 'topic_selection' | 'template_settings' | 'guide_results'>('home');
  const [documentId, setDocumentId] = useState<string | null>(null);
  const [uploadedDocs, setUploadedDocs] = useState<Array<{ documentId: string; filename: string }>>([]);
  const [productName, setProductName] = useState<string>('Product');
  const [recommendations] = useState<TemplateRecommendationType[]>([]);
  const [storyboard, setStoryboard] = useState<Storyboard | null>(null);
  const [activeStep, setActiveStep] = useState<StoryboardStep | null>(null);
  const [selectedTopics, setSelectedTopics] = useState<TopicSuggestion[]>([]);
  const [cachedTopics, setCachedTopics] = useState<TopicSuggestion[] | null>(null);
  const [cachedSelectedTopicIds, setCachedSelectedTopicIds] = useState<string[] | null>(null);
  const [generatedGuides, setGeneratedGuides] = useState<GeneratedGuide[]>([]);
  const [draftId, setDraftId] = useState<string | null>(null);
  const [, setSearchResults] = useState<SearchResults | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedSourceKind, setSelectedSourceKind] = useState<SourceKind | null>(null);
  const [makerBrands, setMakerBrands] = useState<MakerBrand[]>([]);
  const [selectedMakerBrandId, setSelectedMakerBrandId] = useState<string | null>(null);
  const [selectedMakerProductId, setSelectedMakerProductId] = useState<string | null>(null);
  const [makerCreatorId, setMakerCreatorId] = useState<string | null>(null);
  const [makerBootstrapError, setMakerBootstrapError] = useState<string | null>(null);
  const [productWalkthroughError, setProductWalkthroughError] = useState<string | null>(null);
  const { healthStatus: databaseHealthStatus } = useDatabaseHealth();
  const [dbReadyOnce, setDbReadyOnce] = useState(false);
  const [generationSettings, setGenerationSettings] = useState<GenerationSettings>(DEFAULT_GENERATION_SETTINGS);
  useEffect(() => {
    if (databaseHealthStatus.healthy === true) {
      setDbReadyOnce(true);
    }
  }, [databaseHealthStatus.healthy]);

  const dbStarting = !dbReadyOnce && (databaseHealthStatus.checking || databaseHealthStatus.healthy !== true);

  const handleDocumentsUploaded = (docs: Array<{ documentId: string; filename: string }>) => {
    setUploadedDocs(docs);
    if (docs.length === 1) {
      const base = (docs[0].filename || 'Product').replace(/\.[^/.]+$/, '');
      setProductName(base || 'Product');
    } else if (docs.length > 1) {
      setProductName('Combined documentation');
    } else {
      setProductName('Product');
    }
  };

  useEffect(() => {
    // Warm-up instruction-flow services (doc2simpi) via backend `/pf-health`.
    // ACA can scale to zero; warm it up in the background so the first analysis is less likely to wait cold.
    let cancelled = false;
    (async () => {
      await ensurePfReady({ maxWaitMs: 45000 });
      if (cancelled) return;
    })().catch(() => {
      if (cancelled) return;
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!cancelled) setMakerBootstrapError(null);
        const res = await creatorFetch(apiV1Url('/api/v1/me'), { method: 'GET' });
        if (!res.ok) {
          if (res.status === 403 && !cancelled) {
            setMakerBootstrapError('Access managed in Dashboard');
          }
          return;
        }
        const data = (await res.json()) as MakerMeResponse;
        if (cancelled) return;
        const brands = Array.isArray(data.brands) ? data.brands : [];
        setMakerCreatorId(data.user?.entraOid || null);
        setMakerBrands(brands);
        const requestedDashboardBrandId = launchParams.dashboardBrandId || launchParams.brandId;
        const requestedSimpiBrandId = launchParams.simpiBrandId || launchParams.brandId;
        const brandFromQuery = brands.find(
          (b) =>
            (requestedDashboardBrandId && b.brandId === requestedDashboardBrandId) ||
            (requestedSimpiBrandId && b.simpiBrandId === requestedSimpiBrandId)
        );
        const requestedBrandProvided = !!(launchParams.brandId || launchParams.dashboardBrandId || launchParams.simpiBrandId);
        const requestedBrandMissing = requestedBrandProvided && !brandFromQuery;
        const defaultBrandId =
          brandFromQuery?.brandId ||
          data.defaultBrandId ||
          brands[0]?.brandId ||
          null;
        setSelectedMakerBrandId(defaultBrandId);
        const defaultBrand = brands.find((b) => b.brandId === defaultBrandId) || brands[0];

        const products = defaultBrand?.products || [];
        const productFromQuery = launchParams.productId
          ? products.find((p) => p.productId === launchParams.productId)
          : undefined;

        let selectedProductId: string | null = null;
        let bootstrapError: string | null = null;

        if (requestedBrandMissing) {
          bootstrapError = 'No access to requested brand. Request access in Dashboard.';
        } else if (launchParams.productId) {
          // If products are available client-side, validate immediately.
          if (products.length > 0 && !productFromQuery) {
            bootstrapError = 'Requested product is not available for this brand. Please select a valid product.';
            selectedProductId = null;
          } else {
            // Brand-only bootstrap can still carry product context from the editor URL.
            selectedProductId = launchParams.productId;
          }
        } else {
          bootstrapError = 'Open SIMPI Maker from a selected product in the Editor.';
          selectedProductId = null;
        }

        setSelectedMakerProductId(selectedProductId);
        setMakerBootstrapError(bootstrapError);
      } catch {
        if (!cancelled) setMakerBootstrapError('Could not load maker access context');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [launchParams]);

  const handleMakerTargetChange = (brandId: string, productId: string) => {
    setSelectedMakerBrandId(brandId || null);
    setSelectedMakerProductId(productId || null);
    if (brandId && productId) {
      setMakerBootstrapError(null);
    } else if (brandId && !productId) {
      setMakerBootstrapError('Please select a valid product before creating SIMPIs.');
    }
  };

  const handleAnalyzeDocumentation = async () => {
    logger.debug('handleAnalyzeDocumentation', { uploadedDocsCount: uploadedDocs.length });

    if (!uploadedDocs.length) return;

    try {
      setIsLoading(true);
      // Keep downstream contracts as single document_id by combining uploaded docs server-side.
      const ids = uploadedDocs.map(d => d.documentId);
      if (ids.length === 1) {
        setDocumentId(ids[0]);
      } else {
        const resp = await documentApi.combineDocuments(ids, 'Combined documentation');
        if (!resp.success) throw new Error(resp.message || 'Failed to combine documents');
        setDocumentId(resp.data.documentId);
      }
      setCachedTopics(null);
      setCachedSelectedTopicIds(null);

      setCurrentStep('topic_selection');
    } catch (e) {
      logger.error('Analyze documentation failed', e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTopicsSelected = async (topics: TopicSuggestion[]) => {
    logger.debug('handleTopicsSelected', { topicsCount: topics.length });
    
    if (!documentId) {
      logger.error('No document ID available');
      return;
    }

    setSelectedTopics(topics);
    setCurrentStep('template_settings');
  };

  const handleGenerateFromTemplate = async (args: { topics: TopicSuggestion[]; settings: GenerationSettings }) => {
    if (!documentId) return;
    setIsLoading(true);
    setGenerationSettings(args.settings);
    try {
      const response = await creatorFetch(apiV1Url('/api/v1/generate-guides'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          document_id: documentId,
          product_name: productName,
          selected_topics: args.topics,
          template: args.settings.template,
          style: args.settings.style,
          preferred_locale: args.settings.preferredLanguage === 'auto' ? undefined : args.settings.preferredLanguage
        })
      });

      if (!response.ok) {
        throw new Error('Failed to generate guides');
      }

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.message || 'Failed to generate guides');
      }

      setGeneratedGuides(data.data.guides);
      const slug = (productName || 'product')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
      const iso = new Date().toISOString().replace(/[:.]/g, '').replace('Z', 'Z');
      setDraftId(`${slug}/guides/${iso}`);
      setCurrentStep('guide_results');
    } catch (error) {
      logger.error('Guide generation error', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToTopics = () => {
    setCurrentStep('topic_selection');
  };

  const handleStartOver = () => {
    setCurrentStep('home');
    setDocumentId(null);
    setUploadedDocs([]);
    setProductName('Product');
    setSelectedTopics([]);
    setCachedTopics(null);
    setCachedSelectedTopicIds(null);
    setGeneratedGuides([]);
    setDraftId(null);
    setSearchResults(null);
    setSelectedSourceKind(null);
    setProductWalkthroughError(null);
    setGenerationSettings(DEFAULT_GENERATION_SETTINGS);
  };

  const handleChooseUpload = (sourceKind: 'manual' | 'webpage') => {
    setSelectedSourceKind(sourceKind);
    setCurrentStep('upload');
  };

  const handleChooseSearch = () => {
    setSelectedSourceKind('search');
    setCurrentStep('search');
  };

  const handleChooseProductWalkthrough = () => {
    setSelectedSourceKind('product_walkthrough');
    setProductWalkthroughError(null);
    setCurrentStep('product_walkthrough_source');
  };

  const handleIntentSelected = (template: GuideTemplate) => {
    setGenerationSettings((prev) => applyIntentDefaults(template, prev));
    setCurrentStep('source_selection');
  };

  const handleBackToIntentSelection = () => {
    setCurrentStep('home');
  };

  const handleBackToSourceSelection = () => {
    setCurrentStep('source_selection');
  };

  const handleSearchResults = async (results: SearchResults) => {
    setSearchResults(results);
    setProductName(results.product_name);
    setIsLoading(true);
    
    try {
      // Store search results as document content
      logger.debug('Storing search results as document content');
      const response = await creatorFetch(apiV1Url('/api/v1/store-search-results'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(results)
      });

      if (!response.ok) {
        throw new Error('Failed to store search results');
      }

      const data = await response.json();
      
      if (data.success) {
        setDocumentId(data.data.document_id);
        setCurrentStep('topic_selection');
        logger.debug('Search results stored', { documentId: data.data.document_id });
      } else {
        throw new Error(data.message || 'Failed to store search results');
      }
    } catch (error) {
      logger.error('Error storing search results', error);
      // Fallback to mock document ID
      const mockDocId = `search_${Date.now()}`;
      setDocumentId(mockDocId);
      setCurrentStep('topic_selection');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateProductWalkthrough = async (input: { source_url: string; shop: 'amazon' | 'mediamarkt' }) => {
    setIsLoading(true);
    setProductWalkthroughError(null);
    try {
      const response = await creatorFetch(apiV1Url('/api/v1/product-walkthroughs/generate'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(input)
      });

      if (!response.ok) {
        const errorText = await response.text();
        let parsedMessage: string | null = null;
        try {
          const parsedBody = JSON.parse(errorText);
          parsedMessage = typeof parsedBody?.message === 'string' ? parsedBody.message : null;
        } catch {
          // Not JSON (e.g. a raw gateway error page) — fall back to the raw text below.
        }
        throw new Error(parsedMessage || `Failed to generate product walkthrough: ${response.status} ${errorText}`);
      }

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.message || 'Failed to generate product walkthrough');
      }

      setGeneratedGuides(data.data.guides || []);
      setProductName(data.data.product_name || 'Product');
      const slug = (data.data.product_name || 'product-walkthrough')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
      const iso = new Date().toISOString().replace(/[:.]/g, '').replace('Z', 'Z');
      setDraftId(`${slug}/product-walkthrough/${iso}`);
      setCurrentStep('guide_results');
    } catch (error) {
      logger.error('Product walkthrough generation error', error);
      setProductWalkthroughError(error instanceof Error ? error.message : 'Failed to generate product walkthrough');
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportGuide = async (guide: GeneratedGuide, format: 'pdf' | 'word' | 'html') => {
    logger.debug('Exporting guide', { guideId: guide.id, format });
    // TODO: Implement export functionality
  };

  const handleTemplateSelected = async (templateType: string) => {
    logger.debug('handleTemplateSelected', { templateType, documentId });
    
    if (!documentId) {
      logger.error('No document ID available for storyboard generation');
      return;
    }

    setIsLoading(true);
    
    try {
      // Import the storyboardApi
      const { storyboardApi } = await import('./services/api.ts');
      
      logger.debug('Starting storyboard generation...');
      
      // Call the real API
      const response = await storyboardApi.generateStoryboard(
        documentId, 
        templateType,
        {} // preferences - can be expanded later
      );
      
      logger.debug('Storyboard generation response', response);
      
      if (response.success) {
        setStoryboard(response.data);
        setCurrentStep('edit');
      } else {
        logger.error('Storyboard generation failed', response.message);
        // Fall back to mock data
        setStoryboard(mockStoryboard);
        setCurrentStep('edit');
      }
    } catch (error) {
      logger.error('Storyboard generation error', error);
      // Fall back to mock data
      setStoryboard(mockStoryboard);
      setCurrentStep('edit');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStoryboardUpdate = (updatedStoryboard: Storyboard) => {
    setStoryboard(updatedStoryboard);
  };

  const handleStepEdit = (stepId: string) => {
    if (storyboard) {
      const step = storyboard.steps.find(s => s.id === stepId);
      setActiveStep(step || null);
    }
  };

  const handleStepUpdate = (stepId: string, updates: Partial<StoryboardStep>) => {
    if (storyboard) {
      const updatedSteps = storyboard.steps.map(step =>
        step.id === stepId ? { ...step, ...updates } : step
      );
      setStoryboard({ ...storyboard, steps: updatedSteps });
    }
  };

  const handleExport = (format: 'json' | 'pdf' | 'html') => {
    logger.debug('Export storyboard', { format });
    // Implement export logic
  };

  const handlePreview = () => {
    setCurrentStep('preview');
  };

  return (
    <QueryClientProvider client={queryClient}>
      <div
        className="min-h-screen bg-gray-50"
        style={currentStep === 'home' ? START_PAGE_BACKGROUND : undefined}
      >
        {/* Header */}
        <header className="bg-white shadow-sm border-b">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center space-x-4">
                <img
                  src={SIMPI_LOGO_URL}
                  alt="SIMPI"
                  className="h-10 w-auto"
                />
              </div>
              {makerBootstrapError && (
                <span className="text-sm text-amber-700">{makerBootstrapError}</span>
              )}
            </div>
          </div>
        </header>
        {dbStarting && (
          <div className="border-b bg-white border-amber-200">
            <div className="container mx-auto px-4 py-3">
              <p className="text-sm text-amber-800">
                Starting database. This can take up to about 2 minutes after idle.
              </p>
            </div>
          </div>
        )}
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-7xl mx-auto">
            {currentStep === 'home' && (
              <div className="text-center space-y-8">
                <div className="max-w-2xl mx-auto mb-12">
                  <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                    Choose what you want to create
                  </h2>
                  <p className="text-gray-600">
                    Start with the outcome you want, then choose the best source for it
                  </p>
                </div>
                
                <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
                  <div 
                    className="bg-white rounded-lg shadow-lg p-8 hover:shadow-xl transition-shadow cursor-pointer border-2 border-transparent hover:border-blue-500"
                    onClick={() => handleIntentSelected('quickstart')}
                  >
                    <div className="flex flex-col items-center space-y-4">
                      <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                        <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900">Quickstart</h3>
                      <p className="text-gray-600 text-center">
                        A short, first-success guide for new users: setup, first use, key capabilities, and where to go next.
                      </p>
                      <div className="text-sm text-gray-500">
                        Best for onboarding and getting started fast
                      </div>
                    </div>
                  </div>
                  
                  <div 
                    className="bg-white rounded-lg shadow-lg p-8 hover:shadow-xl transition-shadow cursor-pointer border-2 border-transparent hover:border-green-500"
                    onClick={() => handleIntentSelected('product_guide')}
                  >
                    <div className="flex flex-col items-center space-y-4">
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                        <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.483 9.246 5 7.5 5 4.462 5 2 6.791 2 9c0 2.209 2.462 4 5.5 4 1.746 0 3.332-.483 4.5-1.253m0-5.494C13.168 5.483 14.754 5 16.5 5 19.538 5 22 6.791 22 9c0 2.209-2.462 4-5.5 4-1.746 0-3.332-.483-4.5-1.253" />
                        </svg>
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900">Product Guide</h3>
                      <p className="text-gray-600 text-center">
                        A broader set of how-to guides across setup, usage, maintenance, and basic troubleshooting.
                      </p>
                      <div className="text-sm text-gray-500">
                        Best for fuller operational coverage
                      </div>
                    </div>
                  </div>

                  <div
                    className="bg-white rounded-lg shadow-lg p-8 hover:shadow-xl transition-shadow cursor-pointer border-2 border-transparent hover:border-amber-500"
                    onClick={handleChooseProductWalkthrough}
                  >
                    <div className="flex flex-col items-center space-y-4">
                      <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center">
                        <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7h18M5 7v12h14V7M8 11h8M8 15h5M9 3h6l1 4H8l1-4z" />
                        </svg>
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900">Product Walkthrough (Shelf)</h3>
                      <p className="text-gray-600 text-center">
                        A five-step buying walkthrough from an Amazon listing: fit, difference, friction, ownership, and buy prompt.
                      </p>
                      <div className="text-sm text-gray-500">
                        Best for shelf and product-page conversion
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {currentStep === 'source_selection' && (
              <div className="text-center space-y-8">
                <div className="max-w-2xl mx-auto mb-12">
                  <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                    Choose your source
                  </h2>
                  <p className="text-gray-600">
                    {generationSettings.template === 'quickstart'
                      ? 'Use a manual, product page, or search results to generate a focused quickstart.'
                      : 'Use a manual, product page, or search results to generate broader product guides.'}
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                  <div
                    className="bg-white rounded-lg shadow-lg p-8 hover:shadow-xl transition-shadow cursor-pointer border-2 border-transparent hover:border-blue-500"
                    onClick={() => handleChooseUpload('manual')}
                  >
                    <div className="flex flex-col items-center space-y-4">
                      <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                        <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900">Upload Manual</h3>
                      <p className="text-gray-600 text-center">
                        Upload an instruction manual, PDF, or help document when you already have structured product documentation.
                      </p>
                    </div>
                  </div>

                  <div
                    className="bg-white rounded-lg shadow-lg p-8 hover:shadow-xl transition-shadow cursor-pointer border-2 border-transparent hover:border-violet-500"
                    onClick={() => handleChooseUpload('webpage')}
                  >
                    <div className="flex flex-col items-center space-y-4">
                      <div className="w-16 h-16 bg-violet-100 rounded-full flex items-center justify-center">
                        <svg className="w-8 h-8 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.5 12h19" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 2.5c2.5 2.5 4 5.9 4 9.5s-1.5 7-4 9.5c-2.5-2.5-4-5.9-4-9.5s1.5-7 4-9.5z" />
                        </svg>
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900">Paste Webpage URL</h3>
                      <p className="text-gray-600 text-center">
                        Import a product page, listing page, or manufacturer webpage such as Amazon, Shopify, or brand sites.
                      </p>
                    </div>
                  </div>

                  <div
                    className="bg-white rounded-lg shadow-lg p-8 hover:shadow-xl transition-shadow cursor-pointer border-2 border-transparent hover:border-green-500"
                    onClick={handleChooseSearch}
                  >
                    <div className="flex flex-col items-center space-y-4">
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                        <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900">Search Online</h3>
                      <p className="text-gray-600 text-center">
                        Search the web for manuals, product information, and usage guidance when you don&apos;t have a source handy.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex justify-center">
                  <button
                    onClick={handleBackToIntentSelection}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Back
                  </button>
                </div>
              </div>
            )}

            {currentStep === 'upload' && (
              <DocumentUpload
                onDocumentsUploaded={handleDocumentsUploaded}
                onAnalyzeDocumentation={handleAnalyzeDocumentation}
                onBack={handleBackToSourceSelection}
                template={generationSettings.template}
                sourceKind={selectedSourceKind === 'webpage' ? 'webpage' : 'manual'}
              />
            )}

            {currentStep === 'search' && (
              <ProductSearch
                onSearchResults={handleSearchResults}
                onBack={handleBackToSourceSelection}
                generationSettings={generationSettings}
              />
            )}

            {currentStep === 'product_walkthrough_source' && (
              <ProductWalkthroughSource
                onGenerate={handleGenerateProductWalkthrough}
                onBack={handleStartOver}
                isLoading={isLoading}
                externalError={productWalkthroughError}
              />
            )}

            {currentStep === 'topic_selection' && documentId && (
              <TopicSelection
                documentId={documentId}
                productName={productName}
                onTopicsSelected={handleTopicsSelected}
                initialTopics={cachedTopics || undefined}
                initialSelectedTopicIds={cachedSelectedTopicIds || undefined}
                onCacheState={(s) => {
                  setCachedTopics(s.topics);
                  setCachedSelectedTopicIds(s.selectedTopicIds);
                }}
                onBack={handleStartOver}
                generationSettings={generationSettings}
                onGenerationSettingsChange={setGenerationSettings}
                sourceKind={selectedSourceKind || undefined}
                isLoading={isLoading}
              />
            )}

            {currentStep === 'template_settings' && (
              <TemplateAndSettings
                selectedTopics={selectedTopics}
                settings={generationSettings}
                onChangeSettings={setGenerationSettings}
                onBack={handleBackToTopics}
                onGenerate={handleGenerateFromTemplate}
                isLoading={isLoading}
              />
            )}

            {currentStep === 'guide_results' && (
              <GuideResults
                guides={generatedGuides}
                productName={productName}
                draftId={draftId || undefined}
                onExportGuide={handleExportGuide}
                onBack={handleBackToTopics}
                onStartOver={handleStartOver}
                makerCreatorId={makerCreatorId || undefined}
                makerBrands={makerBrands}
                selectedMakerBrandId={selectedMakerBrandId || undefined}
                selectedMakerProductId={selectedMakerProductId || undefined}
                onMakerTargetChange={handleMakerTargetChange}
                generationSettings={generationSettings}
                returnToEditorUrl={launchParams.returnUrl || undefined}
              />
            )}

            {currentStep === 'analyze' && (
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-lg text-gray-600">Analyzing your document...</p>
              </div>
            )}

            {currentStep === 'recommend' && (
              <TemplateRecommendation
                recommendations={recommendations}
                onTemplateSelected={handleTemplateSelected}
                isLoading={isLoading}
              />
            )}

            {currentStep === 'edit' && storyboard && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2">
                  <StoryboardEditor
                    storyboard={storyboard}
                    onStoryboardUpdate={handleStoryboardUpdate}
                    onStepEdit={handleStepEdit}
                    isLoading={isLoading}
                  />
                  <div className="mt-4 flex justify-center">
                    <button
                      onClick={handlePreview}
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Preview Guide
                    </button>
                  </div>
                </div>
                <div>
                  <EnhancedChatAssistant
                    activeStep={activeStep}
                    onStepUpdate={handleStepUpdate}
                    onUndo={() => {}}
                    onRedo={() => {}}
                    canUndo={false}
                    canRedo={false}
                    isLoading={isLoading}
                  />
                </div>
              </div>
            )}

            {currentStep === 'preview' && storyboard && (
              <div>
                <div className="mb-4 text-center">
                  <button
                    onClick={() => setCurrentStep('edit')}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors mr-4"
                  >
                    ← Back to Edit
                  </button>
                </div>
                <PreviewMode
                  storyboard={storyboard}
                  onExport={handleExport}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </QueryClientProvider>
  );
}

export default App;